import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getAllSeverityData as getThreatHuntingGraphDetailsService} from 'services/modules/soc/agent';

import ReactApexChart from 'react-apexcharts';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt } from '@fortawesome/free-solid-svg-icons';

import { Card, CardContent, Box, Button,Typography } from '@mui/material';


const SeverityAreaChart = (props) => {

  // Props
  const { endDate, startDate,agentId,agentName} = props;
  
  // React router
   const navigate = useNavigate();

  //State Variables
  const [chartData, setChartData] = useState({
    series: [],
    options: {},
  });
  const [rawResponse, setRawResponse] = useState(null);
  const [isEmptyChart, setIsEmptyChart] = useState(true);
  const [intervals, setIntervals] = useState("");

  // Redux Selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);


  useEffect(() => {
    if (startDate !== null && endDate !== null) {
      let intervals = calculateIntervalDuration(startDate, endDate, 6);
      setIntervals(intervals);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  // Method handles to fetch the severity data based on group, env and timestamps
  useEffect(() => {
    const fetchThreatHuntingDetails = async () => {
      try {
        if (selectedCompanyContainer?.soc_env && endDate!==null && startDate!== null  && intervals!=="" && intervals!=="0m") {
          const data = await getThreatHuntingGraphDetailsService({ agentId: agentId, soc_env: selectedCompanyContainer?.soc_env, end: endDate, start: startDate,intervals:intervals});
          setRawResponse(data.data);
        }
      } catch (error) {
      }
    };

    if (agentId) {
      fetchThreatHuntingDetails();
    }
  }, [agentId, selectedCompanyContainer?.soc_env, endDate, startDate,intervals]);

  // Handles to generate the time intervals
  const generateTimeIntervals = () => {
    const intervals = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffHours = Math.ceil((end - start) / (1000 * 60 * 60));

    const numIntervals = 6;
    const intervalDuration = Math.floor(diffHours / numIntervals);

    let current = new Date(start);
    for (let i = 0; i < numIntervals; i++) {
      const intervalStart = new Date(current);
      current.setHours(current.getHours() + intervalDuration);
      const intervalEnd = new Date(current);
      const istOffset = 5.5 * 60 * 60 * 1000
      const formatToIST = (date) => {
        const istDate = new Date(date.getTime() + istOffset);
        return `${istDate.getFullYear()}-${String(istDate.getMonth() + 1).padStart(2, "0")}-${String(
          istDate.getDate()
        ).padStart(2, "0")} ${String(istDate.getHours()).padStart(2, "0")}:${String(
          istDate.getMinutes()
        ).padStart(2, "0")}:${String(istDate.getSeconds()).padStart(2, "0")}`;
      };

      intervals.push({
        start: formatToIST(intervalStart),
        end: formatToIST(intervalEnd),
      });

    }
    return intervals;
  };
  const timeIntervals = generateTimeIntervals();

  // Method to calculate the interval duration
  const calculateIntervalDuration = (startDate, endDate, numIntervals) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;

    const diffMinutes = Math.ceil(diffMs / (1000 * 60));
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    let intervalDuration, unit;

    if (diffHours < 1) {
      intervalDuration = Math.floor(diffMinutes / numIntervals);
      unit = "m";
    } else if (diffDays < 6) {
      intervalDuration = Math.floor(diffHours / numIntervals);
      unit = "h";
    } else {
      intervalDuration = Math.floor(diffDays / numIntervals);
      unit = "d";
    }

    return intervalDuration + unit;
  };

  // Method to format the raw response data  
  const processData = (rawData) => {
    const timeIntervals = generateTimeIntervals();
    const intervalCounts = timeIntervals.map(() => ({ medium: 0, high: 0, critical: 0 }));
    const severityBuckets = ['medium', 'high', 'critical'];
    severityBuckets.forEach((severity) => {
      const data = rawData?.aggregations?.severity_ranges?.buckets?.[severity]?.time_buckets?.buckets || [];
      data.forEach((entry) => {
        const entryTime = new Date(entry.key_as_string);
        for (let i = 0; i < timeIntervals.length; i++) {
          const intervalStart = new Date(timeIntervals[i].start.replace(" ", "T"));
          const intervalEnd = new Date(timeIntervals[i].end.replace(" ", "T"));
          if (entryTime >= intervalStart && entryTime < intervalEnd) {
            intervalCounts[i][severity] += entry.doc_count;
            break;
          }
        }
      });
    });
    return {
      categories: timeIntervals.map(interval => convertToIST(interval.start).toLocaleString('en-IN', { hour12: false })),
      series: severityBuckets.map(severity => ({
        name: severity.charAt(0).toUpperCase() + severity.slice(1),
        data: intervalCounts.map(interval => interval[severity]),
      })),
    };
  };

  // Handles to convert the timestamp to IST
  const convertToIST = (key) => {
    if (key) {
      const date = new Date(key);
      let istDate;
      // Regex to match both formats: +0530 and +05:30
      const timezoneOffsetRegex = /[+-]\d{2}:?\d{2}$/;

      // Check if the timestamp contains a valid timezone offset (either +0530 or +05:30)
      if (key.match(timezoneOffsetRegex)) {
        // If the timestamp includes "+0530" or "+05:30", it's already in IST
        istDate = date;
      } else if (key.includes("z") || key.includes("+0000")) {
        // If the timestamp is UTC (includes "z" or "+0000"), convert to IST
        istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
      } else {

        istDate = date;
      }
      return istDate
    }
  }

  // Sets the chart data 
  useEffect(() => {
    if (rawResponse) {
      const processedData = processData(rawResponse);
      const emptyCheck = processedData.series.every(serie => serie.data.every(value => value === 0));
      const formattedArray = processedData?.categories?.map(datetime => {
        const [date, time] = datetime.split(', ');
        return [date, time];
      });
      setChartData({
        series: processedData.series,
        options: {
          chart: { type: 'area', height: 350, zoom: { enabled: false } },
          dataLabels: { enabled: false },
          xaxis: {
            categories: formattedArray,
            labels: {
              show: true,
              style: { fontSize: '12px' },
            },
          },
          tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            x: {
              show: true,
              formatter: function (value, { dataPointIndex }) {
                if (timeIntervals && timeIntervals[dataPointIndex]) {
                  const { start, end } = timeIntervals[dataPointIndex];
                  // return `${convertToIST(start)} - ${convertToIST(end)}`;
                  return `${start.toLocaleString('en-IN', { hour12: false })} - ${end.toLocaleString('en-IN', { hour12: false })}`
                }
                return value;
              },
            },
            y: {
              formatter: function (value, { seriesIndex, series, dataPointIndex }) {
                const allZero =
                  series[0][dataPointIndex] === 0 &&
                  series[1][dataPointIndex] === 0 &&
                  series[2][dataPointIndex] === 0;
                if (allZero) return null;
                else if (series[seriesIndex][dataPointIndex] === 0) {
                  return null;
                }
                return `${value}`;
              },
            },
          },
          legend: { position: "top", horizontalAlign: "center", show: true },
          colors: ["#4e4ece", "#fc659c", "red"],
        },
      });
      setIsEmptyChart(emptyCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawResponse]);

  return (
    <Card sx={{ margin: '10px', height: '450px' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '25px', marginRight: '10px' }} />
            <Typography variant="h6">Threat Hunting</Typography>
          </div>
                    {!isEmptyChart && (
                      <Button
                        variant="contained"
                        onClick={() =>
                          navigate(`/soc/security-alerts/?agentname=${agentName}&agentid=${agentId}`, {
                            state: { end: endDate, start: startDate },
                          })
                        }
                      >
                        View All
                      </Button>
                    )}
        </Box>
        <div style={{ position: 'relative' }}  className="soc-graph-container">
          <ReactApexChart options={chartData.options} series={chartData.series} type="area" height={350} />
          {isEmptyChart && (
            <div
              className="blur-effect"
            >
              No Details Found
            </div>
           
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SeverityAreaChart;
